import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { KaiLogoWithName } from '@/Client/Icons';
import { useAuth, useKaiProApiClient } from '../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';

const PAGE_STYLE = css`
  label: CONTRACTOR_CLAIM;
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  background: var(--background);
  padding: ${LAYOUT2.spacing3}px;
`;

const CARD_STYLE = css`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
  text-align: center;
  align-items: center;
`;

const TITLE_STYLE = css`${TYPOGRAPHY2.largeBold} color: var(--text);`;
const SUBTLE_STYLE = css`${TYPOGRAPHY2.small} color: var(--text2);`;

/**
 * Lands a contractor who clicked their emailed/texted claim link. Clicking the link is the
 * proof of contact control, so we immediately create/claim the org, store the returned session,
 * and deep-link into the project (dashboard). Single-use: a consumed/expired link shows an error.
 */
export const ContractorClaimPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const apiClient = useKaiProApiClient();
  const { credentialsMutators } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const claimedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setError('This link is missing its token.');
      return;
    }
    if (claimedRef.current) return; // guard StrictMode double-invoke — the claim link is single-use
    claimedRef.current = true;

    apiClient.claimContractorOrg(token)
      .then(async ({ token: sessionToken }) => {
        await credentialsMutators.setToken(sessionToken);
        navigate('/contractor-dashboard', { replace: true });
      })
      .catch(() => setError('This confirmation link is invalid or has expired. Please open your project link again to request a new one.'));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div css={PAGE_STYLE}>
      <div css={CARD_STYLE}>
        <KaiLogoWithName />
        {error
          ? (
            <>
              <div css={TITLE_STYLE}>Link unavailable</div>
              <div css={SUBTLE_STYLE}>{error}</div>
            </>
          )
          : <div css={SUBTLE_STYLE}>Setting up your account…</div>}
      </div>
    </div>
  );
};
